/** Virtual project root ids for cloud-backed web projects. Desktop uses real paths. */

export const CLOUD_ROOT_PREFIX = "cloud://";
export const DEMO_ROOT_PREFIX = "demo://";

export type ProjectRootKind = "disk" | "cloud" | "demo";

export function isCloudRoot(rootPath: string): boolean {
  return rootPath.startsWith(CLOUD_ROOT_PREFIX);
}

export function isDemoRoot(rootPath: string): boolean {
  return rootPath.startsWith(DEMO_ROOT_PREFIX);
}

export function isVirtualRoot(rootPath: string): boolean {
  return isCloudRoot(rootPath) || isDemoRoot(rootPath);
}

/** Stable demo project id, e.g. `demo://little-women`. */
export function demoProjectRoot(projectId = "little-women"): string {
  const id = projectId.trim();
  if (id === "" || id.includes("/") || id.includes("\\")) {
    throw new Error("Invalid demo project id");
  }
  return `${DEMO_ROOT_PREFIX}${id}`;
}

export function demoProjectId(rootPath: string): string {
  if (isDemoRoot(rootPath) === false) {
    throw new Error("Not a demo project root");
  }
  return rootPath.slice(DEMO_ROOT_PREFIX.length);
}

/** Parse `demo://id/relative/path` into root + project-relative path. */
export function parseDemoJoinedPath(joinedPath: string): {
  rootPath: string;
  relative: string;
} {
  const normalized = joinedPath.replace(/\\/g, "/");
  if (normalized.startsWith(DEMO_ROOT_PREFIX) === false) {
    throw new Error("Not a demo project path");
  }
  const rest = normalized.slice(DEMO_ROOT_PREFIX.length);
  const slash = rest.indexOf("/");
  if (slash === -1) {
    return { rootPath: `${DEMO_ROOT_PREFIX}${rest}`, relative: "" };
  }
  const id = rest.slice(0, slash);
  return {
    rootPath: `${DEMO_ROOT_PREFIX}${id}`,
    relative: rest.slice(slash + 1),
  };
}

export function isDemoJoinedPath(path: string): boolean {
  return path.replace(/\\/g, "/").startsWith(DEMO_ROOT_PREFIX);
}

/** Stable cloud project id, e.g. `cloud://550e8400-e29b-41d4-a716-446655440000`. */
export function cloudProjectRoot(projectId: string): string {
  const id = projectId.trim();
  if (id === "" || id.includes("/") || id.includes("\\")) {
    throw new Error("Invalid cloud project id");
  }
  return `${CLOUD_ROOT_PREFIX}${id}`;
}

export function cloudProjectId(rootPath: string): string {
  if (isCloudRoot(rootPath) === false) {
    throw new Error("Not a cloud project root");
  }
  return rootPath.slice(CLOUD_ROOT_PREFIX.length);
}

/** Parse `cloud://id/relative/path` into root + project-relative path. */
export function parseCloudJoinedPath(joinedPath: string): {
  rootPath: string;
  relative: string;
} {
  const normalized = joinedPath.replace(/\\/g, "/");
  if (normalized.startsWith(CLOUD_ROOT_PREFIX) === false) {
    throw new Error("Not a cloud project path");
  }
  const rest = normalized.slice(CLOUD_ROOT_PREFIX.length);

  // Share sessions use `cloud://share/<uuid>/…` (#100).
  if (rest.startsWith("share/")) {
    const after = rest.slice("share/".length);
    const slash = after.indexOf("/");
    if (slash === -1) {
      return { rootPath: `${CLOUD_ROOT_PREFIX}share/${after}`, relative: "" };
    }
    return {
      rootPath: `${CLOUD_ROOT_PREFIX}share/${after.slice(0, slash)}`,
      relative: after.slice(slash + 1),
    };
  }

  const slash = rest.indexOf("/");
  if (slash === -1) {
    return { rootPath: `${CLOUD_ROOT_PREFIX}${rest}`, relative: "" };
  }
  const id = rest.slice(0, slash);
  return {
    rootPath: `${CLOUD_ROOT_PREFIX}${id}`,
    relative: rest.slice(slash + 1),
  };
}

/** True for share-session roots (`cloud://share/<uuid>`). */
export function isShareCloudRoot(rootPath: string): boolean {
  return rootPath.startsWith(`${CLOUD_ROOT_PREFIX}share/`);
}

export function isCloudJoinedPath(path: string): boolean {
  return path.replace(/\\/g, "/").startsWith(CLOUD_ROOT_PREFIX);
}

/** Map a joined project path back to a root-relative segment. */
export function relativeProjectPath(
  rootPath: string,
  joinedPath: string,
): string {
  const normalizedRoot = rootPath.replace(/\\/g, "/").replace(/\/+$/, "");
  const normalized = joinedPath.replace(/\\/g, "/");
  if (normalized === normalizedRoot) return "";
  const prefix = `${normalizedRoot}/`;
  if (normalized.startsWith(prefix) === false) {
    throw new Error("Path is outside the project root");
  }
  return normalized.slice(prefix.length);
}

/** @deprecated Use isCloudRoot */
export const isOpfsRoot = isCloudRoot;
/** @deprecated Use cloudProjectRoot */
export const opfsProjectRoot = cloudProjectRoot;
/** @deprecated Use parseCloudJoinedPath */
export const parseOpfsJoinedPath = parseCloudJoinedPath;
/** @deprecated Use isCloudJoinedPath */
export const isOpfsJoinedPath = isCloudJoinedPath;
